import { RotateCcw, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Input } from "@/components/ui/input";
import { type AppRegistrationRequestDetail, useAppRegistrationQueue } from "@/lib/api";
import { useUser } from "@/lib/user-context";

import { ApproveDialog } from "./approve-dialog";
import { ReconciliationPanel } from "./reconciliation-panel";
import { RejectDialog } from "./reject-dialog";
import { RequestTable } from "./request-table";

export function AdminAppRegistrationsPage() {
  const { user } = useUser();
  const { data: requests = [], isLoading, isError } = useAppRegistrationQueue();
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [approveRequest, setApproveRequest] = useState<AppRegistrationRequestDetail | null>(null);
  const [rejectRequest, setRejectRequest] = useState<AppRegistrationRequestDetail | null>(null);

  const failed = requests.filter((r) => r.status === "failed");
  const failedCount = failed.length;
  const failedDecommissions = failed.filter((r) => r.request_type === "delete").length;
  const failedChanges = failedCount - failedDecommissions;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = q
      ? requests.filter(
          (r) =>
            r.project_name.toLowerCase().includes(q) ||
            r.requester_email.toLowerCase().includes(q) ||
            r.display_name.toLowerCase().includes(q) ||
            r.purpose.toLowerCase().includes(q),
        )
      : requests;
    // The endpoint returns newest-first; lift the `failed` rows above it. They
    // are approved work that stalled halfway through creating directory
    // objects, so they are the most urgent thing in the queue, not the oldest.
    // Subtracting the two booleans keeps the comparator consistent: the
    // `a.status === "failed" ? -1 : 1` form returned 1 for both compare(x, y)
    // and compare(y, x) whenever neither was `failed`, which lets the sort
    // reorder the rest freely and loses the newest-first order this preserves.
    return [...matches].sort(
      (a, b) => Number(b.status === "failed") - Number(a.status === "failed"),
    );
  }, [requests, search]);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-primary text-2xl font-normal">App registrations</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Requests awaiting an approver &mdash; {requests.length} in the queue
            {failedCount > 0 && `, ${failedCount} needing a retry`}
          </p>
        </div>
      </div>

      {failedCount > 0 && (
        <div className="mt-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
          <RotateCcw className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
              {[
                failedChanges > 0 &&
                  `${failedChanges} request${failedChanges === 1 ? "" : "s"} stalled while being applied in Entra`,
                failedDecommissions > 0 &&
                  `${failedDecommissions} decommission${failedDecommissions === 1 ? "" : "s"} stalled while deleting from Entra`,
              ]
                .filter(Boolean)
                .join("; ")}
            </p>
            <p className="text-muted-foreground text-xs">
              Their approval already stands. Open the row and approve again &mdash; the run resumes
              from where it stopped rather than starting over.
            </p>
          </div>
        </div>
      )}

      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        <div className="border-border border-b px-4 py-3">
          <div className="relative max-w-sm">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by project, requester or purpose..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground py-16 text-center text-sm">Loading requests...</div>
        ) : isError ? (
          <div className="text-destructive py-16 text-center text-sm">
            Could not load the approver queue.
          </div>
        ) : (
          <RequestTable
            requests={visible}
            currentUserEmail={user?.email ?? null}
            expandedId={expandedId}
            onToggleExpanded={(requestId) =>
              setExpandedId((current) => (current === requestId ? null : requestId))
            }
            onApprove={setApproveRequest}
            onReject={setRejectRequest}
          />
        )}
      </div>

      <ReconciliationPanel />

      <ApproveDialog
        request={approveRequest}
        open={approveRequest !== null}
        onOpenChange={(open) => {
          if (!open) {
            setApproveRequest(null);
          }
        }}
      />

      <RejectDialog
        request={rejectRequest}
        open={rejectRequest !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRejectRequest(null);
          }
        }}
      />
    </div>
  );
}
